import { Module } from 'vuex';

import { Actions, ShiftCMSState } from '@/store';
import { InfoItem } from '@/models/info-item.model';
import { Action } from '@/models/action.model';
import { Hero } from '@/models/hero.model';
import { QuizQuestion, UserAnswerStats } from '@/models/quiz-question.model';
import { InfoGraphic } from '@/models/info-graphic.model';
import { Initiative } from '@/models/initiative.model';

import infoItemsService from '@/services/info-items.service';
import initiativesService from '@/services/initiatives.service';
import scheduleService from '@/services/schedule-item.service';
import heroesService from '@/services/heroes.service';
import actionsService from '@/services/actions.service';
import questionsService from '@/services/questions.service';
import storiesService from '@/services/stories.service';
import infoGraphicsService from '@/services/info-graphics.service';
import { ScheduleItem } from '@/models/schedule-item.model';
import { Discount } from '@/models/discounts.model';
import discountService from '@/services/discount.service';
import contactService from '@/services/contact.service';
import { Contact } from '@/models/contact.model';
import { FilterData } from '@/services/users.service';

export interface ContentStoreState {
  infoItems: InfoItem[];
  initiatives: Initiative[];
  scheduleItems: ScheduleItem[];
  discounts: Discount[];
  infoGraphics: InfoGraphic[];
  heroes: Hero[];
  actions: Action[];
  questions: QuizQuestion[];
  questionStats: UserAnswerStats;
}

export enum ContentStoreMutations {
  SetInfoItems = 'cns/SetInfoItems',
  SetInitiatives = 'cns/SetInitiatives',
  SetScheduleItems = 'cns/SetScheduleItems',
  SetDiscounts = 'cns/SetDiscounts',
  SetInfoGraphics = 'cns/SetInfoGraphics',
  SetHeroes = 'cns/SetHeroes',
  SetActions = 'cns/SetActions',
  SetQuizQuestions = 'cns/SetQuizQuestions',
  SetQuizQuestionStats = 'cns/SetQuizQuestionStats',
}

export enum ContentStoreActions {
  GetInfoItems = 'cns/GetInfoItems',
  GetInfoGraphics = 'cns/GetInfoGraphics',
  GetInitiatives = 'cns/GetInitiatives',
  GetInitiativesPaginated = 'cns/GetInitiativesPaginated',
  GetInitiativesByContentEditor = 'cns/GetInitiativesByContentEditor',
  GetSchedules = 'cns/GetSchedules',
  GetDiscount = 'cns/GetDiscount',
  GetDiscounts = 'cns/GetDiscounts',
  GetActions = 'cns/GetActions',
  GetHeroes = 'cns/GetHeroes',
  GetQuizQuestions = 'cns/GetQuizQuestions',
  GetInfoItem = 'cns/GetInfoItem',
  GetInfoGraphic = 'cns/GetInfoGraphic',
  GetInitiative = 'cns/GetInitiative',
  GetInitiativeByUUID = 'cns/GetInitiativeByUUID',
  GetAction = 'cns/GetAction',
  GetHero = 'cns/GetHero',
  GetQuizQuestion = 'cns/GetQuizQuestion',
  GetQuizQuestionStats = 'cns/GetQuizQuestionStats',
  GetStory = 'cns/GetStory',
  UpdateInfoItem = 'cns/UpdateInfoItem',
  UpdateInitiative = 'cns/UpdateInitiative',
  UpdateInitiativeByUUID = 'cns/UpdateInitiativeByUUID',
  UpdateInitiativeStatusByUUID = 'cns/UpdateInitiativeStatusByUUID',
  UpdateInfoGraphic = 'cns/UpdateInfoGraphic',
  UpdateHero = 'cns/UpdateHero',
  UpdateQuizQuestion = 'cns/UpdateQuizQuestion',
  UpdateScheduleItem = 'cns/UpdateScheduleItem',
  UpdateDiscount = 'cns/UpdateDiscount',
  UpdateAction = 'cns/UpdateAction',
  RemoveContact = 'cns/RemoveContact',
  CreateAction = 'cns/CreateAction',
  CreateInitiative = 'cns/CreateInitiative',
  CreateScheduleItem = 'cns/CreateScheduleItem',
  CreateDiscount = 'cns/CreateDiscount',
  CreateInfoGraphic = 'cns/CreateInfoGraphic',
  DeleteInfoGraphic = 'cns/DeleteInfoGraphic',
  DeleteScheduleItem = 'cns/DeleteScheduleItem',
  DeleteDiscount = 'cns/DeleteDiscount',
  DeleteAction = 'cns/DeleteAction',
  DeleteInitiative = 'cns/DeleteInitiative',
}

const sn = (str: ContentStoreActions | ContentStoreMutations): string =>
  str.replace(/cns\//g, '');

export const contentStore: Module<ContentStoreState, ShiftCMSState> = {
  namespaced: true,
  state: {
    initiatives: [],
    scheduleItems: [],
    discounts: [],
    infoItems: [],
    infoGraphics: [],
    heroes: [],
    actions: [],
    questions: [],
    questionStats: {
      totalAnswers: 0,
      answerStats: [],
    },
  },
  mutations: {
    [sn(ContentStoreMutations.SetActions)](state, actions: Action[]) {
      state.actions = actions;
    },
    [sn(ContentStoreMutations.SetScheduleItems)](
      state,
      scheduleItems: ScheduleItem[],
    ) {
      state.scheduleItems = scheduleItems;
    },
    [sn(ContentStoreMutations.SetDiscounts)](state, discounts: Discount[]) {
      state.discounts = discounts;
    },
    [sn(ContentStoreMutations.SetHeroes)](state, heroes: Hero[]) {
      state.heroes = heroes;
    },
    [sn(ContentStoreMutations.SetInfoItems)](state, infoItems: InfoItem[]) {
      state.infoItems = infoItems;
    },
    [sn(ContentStoreMutations.SetInitiatives)](
      state,
      initiatives: Initiative[],
    ) {
      state.initiatives = initiatives;
    },
    [sn(ContentStoreMutations.SetInfoGraphics)](
      state,
      infoGraphics: InfoGraphic[],
    ) {
      state.infoGraphics = infoGraphics;
    },
    [sn(ContentStoreMutations.SetQuizQuestions)](
      state,
      questions: QuizQuestion[],
    ) {
      state.questions = questions;
    },
    [sn(ContentStoreMutations.SetQuizQuestionStats)](
      state,
      stats: UserAnswerStats,
    ) {
      state.questionStats = stats;
    },
  },
  actions: {
    async [sn(ContentStoreActions.GetActions)]({ commit }) {
      const res = await actionsService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetActions), res.data);
      }
    },
    async [sn(ContentStoreActions.GetHeroes)]({ commit }) {
      const res = await heroesService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetHeroes), res.data);
      }
    },
    async [sn(ContentStoreActions.GetQuizQuestions)]({ commit }) {
      const res = await questionsService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetQuizQuestions), res.data);
      }
    },
    async [sn(ContentStoreActions.GetInfoItems)]({ commit }) {
      const res = await infoItemsService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetInfoItems), res.data);
      }
    },
    async [sn(ContentStoreActions.GetInitiatives)]({ commit }) {
      const res = await initiativesService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetInitiatives), res.data);
      }
    },
    async [sn(ContentStoreActions.GetInitiativesPaginated)](
      { commit },
      filters: FilterData,
    ) {
      const res = await initiativesService.getListPaginated(filters);
      if (res.status) {
        commit(sn(ContentStoreMutations.SetInitiatives), res.data.items);
      }
    },
    async [sn(ContentStoreActions.GetInitiativesByContentEditor)]({ commit }) {
      const res = await initiativesService.getListForContentUser();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetInitiatives), res.data);
      }
    },
    async [sn(ContentStoreActions.GetSchedules)]({ commit }) {
      const res = await scheduleService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetScheduleItems), res.data);
      }
    },
    async [sn(ContentStoreActions.GetDiscount)](_, id: number) {
      const res = await discountService.get(id);

      return res.data;
    },
    async [sn(ContentStoreActions.GetDiscounts)]({ commit }) {
      const res = await discountService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetDiscounts), res.data);
      }
    },
    async [sn(ContentStoreActions.GetInfoGraphics)]({ commit }) {
      const res = await infoGraphicsService.getList();
      if (res.status) {
        commit(sn(ContentStoreMutations.SetInfoGraphics), res.data);
      }
    },
    async [sn(ContentStoreActions.GetAction)](_, id: number) {
      const res = await actionsService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetHero)](_, id: number) {
      const res = await heroesService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetQuizQuestion)](_, id: number) {
      const res = await questionsService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetQuizQuestionStats)](
      { commit },
      id: number,
    ) {
      const res = await questionsService.getStats(id);
      if (res.status) {
        commit(sn(ContentStoreMutations.SetQuizQuestionStats), res.data);
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetInfoItem)](_, id: number) {
      const res = await infoItemsService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetInfoGraphic)](_, id: number) {
      const res = await infoGraphicsService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetInitiative)](_, id: number) {
      const res = await initiativesService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetInitiativeByUUID)](_, uuid: string) {
      const res = await initiativesService.getByUUID(uuid);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.GetStory)](
      _,
      { type, id }: { type: string; id: number },
    ) {
      const res = await storiesService.getStoryForType(type, id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(ContentStoreActions.CreateAction)](_, action: Action) {
      return await actionsService.create(action);
    },
    async [sn(ContentStoreActions.UpdateAction)](_, action: Action) {
      return await actionsService.edit(action);
    },
    async [sn(ContentStoreActions.CreateInitiative)](
      _,
      initiative: Initiative,
    ) {
      return await initiativesService.create(initiative);
    },
    async [sn(ContentStoreActions.CreateScheduleItem)](_, item: ScheduleItem) {
      return await scheduleService.create(item);
    },
    async [sn(ContentStoreActions.UpdateScheduleItem)](_, item: ScheduleItem) {
      return await scheduleService.edit(item);
    },
    async [sn(ContentStoreActions.CreateDiscount)](_, item: Discount) {
      return await discountService.create(item);
    },
    async [sn(ContentStoreActions.UpdateDiscount)](_, item: Discount) {
      return await discountService.edit(item);
    },
    async [sn(ContentStoreActions.UpdateInitiative)](
      { state, commit },
      initiative: Initiative,
    ) {
      const result = await initiativesService.edit(initiative);

      commit(sn(ContentStoreMutations.SetInitiatives), [
        ...state.initiatives.filter((i) => i.id !== result.data.id),
        result.data,
      ]);

      return result;
    },
    async [sn(ContentStoreActions.UpdateInitiativeByUUID)](
      { state, commit, dispatch },
      { uuid, initiative }: { uuid: string; initiative: Initiative },
    ) {
      const result = await initiativesService.editByUUID(uuid, initiative);

      if (result.status) {
        commit(sn(ContentStoreMutations.SetInitiatives), [
          ...state.initiatives.filter((i) => i.id !== result.data.id),
          result.data,
        ]);
        dispatch(
          Actions.ShowNotification,
          {
            message: 'Saved initiative',
            type: 'success',
          },
          { root: true },
        );
      } else {
        dispatch(
          Actions.ShowNotification,
          {
            message: result.message,
            type: 'warning',
          },
          { root: true },
        );
      }

      return result.data;
    },
    async [sn(ContentStoreActions.UpdateInitiativeStatusByUUID)](
      { state, commit, dispatch },
      { uuid, initiative }: { uuid: string; initiative: Initiative },
    ) {
      const result = await initiativesService.editStatusByUUID(
        uuid,
        initiative,
      );

      if (result.status) {
        commit(sn(ContentStoreMutations.SetInitiatives), [
          ...state.initiatives.filter((i) => i.id !== result.data.id),
          result.data,
        ]);
        dispatch(
          Actions.ShowNotification,
          {
            message: 'Saved initiative',
            type: 'success',
          },
          { root: true },
        );
      } else {
        dispatch(
          Actions.ShowNotification,
          {
            message: result.message,
            type: 'warning',
          },
          { root: true },
        );
      }

      return result.data;
    },
    async [sn(ContentStoreActions.RemoveContact)](_, item: Contact) {
      return await contactService.delete(item.id);
    },
    async [sn(ContentStoreActions.UpdateHero)](_, hero: Hero) {
      return await heroesService.edit(hero);
    },
    async [sn(ContentStoreActions.UpdateQuizQuestion)](
      _,
      quizQuestion: QuizQuestion,
    ) {
      return await questionsService.edit(quizQuestion);
    },
    async [sn(ContentStoreActions.UpdateInfoItem)](_, infoItem: InfoItem) {
      return await infoItemsService.edit(infoItem);
    },
    async [sn(ContentStoreActions.CreateInfoGraphic)](
      _,
      infoGraphic: InfoGraphic,
    ) {
      return await infoGraphicsService.create(infoGraphic);
    },
    async [sn(ContentStoreActions.UpdateInfoGraphic)](
      _,
      infoGraphic: InfoGraphic,
    ) {
      return await infoGraphicsService.edit(infoGraphic);
    },
    async [sn(ContentStoreActions.DeleteInfoGraphic)](
      { dispatch },
      id: number,
    ) {
      const res = await infoGraphicsService.delete(id);

      dispatch(sn(ContentStoreActions.GetInfoGraphics));

      return res.status;
    },
    async [sn(ContentStoreActions.DeleteAction)]({ dispatch }, id: number) {
      const res = await actionsService.delete(id);

      dispatch(sn(ContentStoreActions.GetActions));

      return res.status;
    },
    async [sn(ContentStoreActions.DeleteInitiative)]({ dispatch }, id: number) {
      const res = await initiativesService.delete(id);

      dispatch(sn(ContentStoreActions.GetInitiatives));

      return res.status;
    },
    async [sn(ContentStoreActions.DeleteScheduleItem)](
      { dispatch },
      id: number,
    ) {
      const res = await scheduleService.delete(id);

      dispatch(sn(ContentStoreActions.GetSchedules));

      return res.status;
    },
    async [sn(ContentStoreActions.DeleteDiscount)]({ dispatch }, id: number) {
      const res = await discountService.delete(id);

      dispatch(sn(ContentStoreActions.GetDiscounts));

      return res.status;
    },
  },
};
